// src/js/modules/i18n.js
/**
 * Internationalization (i18n) Module
 * Centralized translations for all navigation and UI components
 * @version 1.0.0
 */

const i18n = {
  // Current language
  currentLanguage: 'fi',

  // All translations
  translations: {
    fi: {
      // Navigation (navs.js)
      nav: {
        mainMenuOpen: 'Päävalikko avattu',
        mainMenuClosed: 'Päävalikko suljettu',
        mainMenuClosedFocusReturned: 'Päävalikko suljettu. Fokus palautettu valikkopainikkeeseen.',
        subMenuOpen: 'Alavalikko avattu',
        subMenuClosed: 'Alavalikko suljettu',
        openMainMenu: 'Avaa päävalikko',
        closeMainMenu: 'Sulje päävalikko',
        openSubMenu: 'Avaa alavalikko',
        closeSubMenu: 'Sulje alavalikko',
        navigation: 'Navigaatio',
        skipToContent: 'Siirry sisältöön',
      },
      // Sidemenu (sidemenu.js)
      sidemenu: {
        openTab: 'Avaa',
        closeTab: 'Sulje',
        tableOfContents: 'sisällysluettelo',
        share: 'jakaminen',
        menuOpened: 'avattu',
        menuClosed: 'Valikko suljettu',
        menuClosedFocusReturned: 'Valikko suljettu. Fokus palautettu painikkeeseen.',
        navigatedTo: 'Siirrytty kohtaan:',
        menu: 'Valikko',
      },
      // Common
      common: {
        close: 'Sulje',
        open: 'Avaa',
        menu: 'Valikko',
      },
    },
    sv: {
      // Navigation (navs.js)
      nav: {
        mainMenuOpen: 'Huvudmenyn öppnad',
        mainMenuClosed: 'Huvudmenyn stängd',
        mainMenuClosedFocusReturned: 'Huvudmenyn stängd. Fokus återställd till menyknappen.',
        subMenuOpen: 'Undermeny öppnad',
        subMenuClosed: 'Undermeny stängd',
        openMainMenu: 'Öppna huvudmenyn',
        closeMainMenu: 'Stäng huvudmenyn',
        openSubMenu: 'Öppna undermeny',
        closeSubMenu: 'Stäng undermeny',
        navigation: 'Navigering',
        skipToContent: 'Hoppa till innehåll',
      },
      // Sidemenu (sidemenu.js)
      sidemenu: {
        openTab: 'Öppna',
        closeTab: 'Stäng',
        tableOfContents: 'innehållsförteckning',
        share: 'delning',
        menuOpened: 'öppnad',
        menuClosed: 'Menyn stängd',
        menuClosedFocusReturned: 'Menyn stängd. Fokus återställd till knappen.',
        navigatedTo: 'Navigerat till:',
        menu: 'Meny',
      },
      // Common
      common: {
        close: 'Stäng',
        open: 'Öppna',
        menu: 'Meny',
      },
    },
    en: {
      // Navigation (navs.js)
      nav: {
        mainMenuOpen: 'Main menu opened',
        mainMenuClosed: 'Main menu closed',
        mainMenuClosedFocusReturned: 'Main menu closed. Focus returned to menu button.',
        subMenuOpen: 'Submenu opened',
        subMenuClosed: 'Submenu closed',
        openMainMenu: 'Open main menu',
        closeMainMenu: 'Close main menu',
        openSubMenu: 'Open submenu',
        closeSubMenu: 'Close submenu',
        navigation: 'Navigation',
        skipToContent: 'Skip to content',
      },
      // Sidemenu (sidemenu.js)
      sidemenu: {
        openTab: 'Open',
        closeTab: 'Close',
        tableOfContents: 'table of contents',
        share: 'share',
        menuOpened: 'opened',
        menuClosed: 'Menu closed',
        menuClosedFocusReturned: 'Menu closed. Focus returned to button.',
        navigatedTo: 'Navigated to:',
        menu: 'Menu',
      },
      // Common
      common: {
        close: 'Close',
        open: 'Open',
        menu: 'Menu',
      },
    },
  },

  /**
   * Detect page language from html lang attribute or URL
   * @returns {string} Language code (fi, sv, or en)
   */
  detectLanguage() {
    // Try to get language from html lang attribute
    const htmlLang = document.documentElement.lang;
    if (htmlLang) {
      const lang = htmlLang.toLowerCase().substring(0, 2);
      if (['fi', 'sv', 'en'].includes(lang)) {
        return lang;
      }
    }

    // Try to detect from URL patterns
    const path = window.location.pathname;

    if (path.includes('arsberattelse') || path.includes('/sv/')) {
      return 'sv';
    }

    if (path.includes('annualreport') || path.includes('/en/')) {
      return 'en';
    }

    return 'fi';
  },

  /**
   * Initialize i18n module
   * @returns {void}
   */
  init() {
    this.currentLanguage = this.detectLanguage();
    console.log('[i18n] Initialized with language:', this.currentLanguage);
  },

  /**
   * Get translation by key path (e.g., 'nav.mainMenuOpen')
   * @param {string} keyPath - Dot-notation key path
   * @param {string} [lang] - Optional language override
   * @returns {string} Translated text
   */
  t(keyPath, lang) {
    const language = lang || this.currentLanguage;
    const keys = keyPath.split('.');
    let value = this.translations[language];

    for (const key of keys) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        console.warn(`[i18n] Translation not found for key: ${keyPath} (${language})`);
        return keyPath;
      }
    }

    return value || keyPath;
  },

  /**
   * Get all translations for a namespace (e.g., 'nav', 'sidemenu')
   * @param {string} namespace - Translation namespace
   * @param {string} [lang] - Optional language override
   * @returns {Object} Translation object
   */
  getNamespace(namespace, lang) {
    const language = lang || this.currentLanguage;
    return (this.translations[language] && this.translations[language][namespace]) || {};
  },

  /**
   * Get current language
   * @returns {string} Current language code
   */
  getLanguage() {
    return this.currentLanguage;
  },

  /**
   * Set language
   * @param {string} lang - Language code
   * @returns {void}
   */
  setLanguage(lang) {
    if (['fi', 'sv', 'en'].includes(lang)) {
      this.currentLanguage = lang;
      console.log('[i18n] Language changed to:', lang);
    } else {
      console.warn('[i18n] Unsupported language:', lang);
    }
  },
};

export default i18n;
